/**
 * VehicleResourcesTab - Vehicle consumables, wear items, and lifecycle scheduling
 *
 * Displays:
 * - Fluid levels (Fuel, AdBlue, Oil, Coolant, Washer)
 * - Wear items (Brake pads, Tires)
 * - Lifecycle schedules (Next due dates with auto-calculation)
 * - Manual entry + telematics data
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Gauge, Droplet, Circle, Calendar, Plus, RefreshCw, ClipboardCheck,
  ChevronDown, ChevronRight, Clock, Edit2, Shield
} from 'lucide-react';
import { vehicleResourceAPI } from '../../../api/vehicles.js';
import { ResourceGauge } from '../resources/ResourceGauge.jsx';
import { WearItemBar } from '../resources/WearItemBar.jsx';
import { ResourceAlertSummary } from '../resources/ResourceStatusBadge.jsx';
import { ResourceEntryForm } from '../resources/ResourceEntryForm.jsx';
import { useFormat } from '../../../i18n/format.js';

// Accordion component for sections
function ResourceSection({ title, icon: Icon, children, defaultOpen = false, badge = null }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <Icon size={18} className="text-gray-600" />
          <span className="font-medium text-gray-800">{title}</span>
          {badge}
        </div>
      </button>
      {isOpen && (
        <div className="p-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

// Schedule row component
function ScheduleRow({ schedule, onEdit }) {
  const { fmt } = useFormat();
  const daysUntil = schedule.days_until_due;
  const isOverdue = daysUntil !== null && daysUntil < 0;
  const isDueSoon = daysUntil !== null && daysUntil >= 0 && daysUntil <= 14;

  const RESOURCE_LABELS = {
    oil_level: 'Niveau huile',
    coolant_level: 'Liquide refroidissement',
    brake_pad_front: 'Plaquettes frein AV',
    brake_pad_rear: 'Plaquettes frein AR',
    tire_front_left: 'Pneu AVG',
    tire_front_right: 'Pneu AVD',
    tire_rear_left: 'Pneu ARG',
    tire_rear_right: 'Pneu ARD',
  };

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${
      isOverdue ? 'bg-red-50' : isDueSoon ? 'bg-amber-50' : 'bg-gray-50'
    }`}>
      <div className="flex items-center gap-3">
        <Calendar size={16} className={isOverdue ? 'text-red-500' : isDueSoon ? 'text-amber-500' : 'text-gray-500'} />
        <div>
          <span className="font-medium text-gray-800">
            {RESOURCE_LABELS[schedule.resource_type] || schedule.resource_type}
          </span>
          {schedule.is_manual_override && (
            <span className="ml-2 text-xs text-blue-600">(manuel)</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4">
        {/* Due date */}
        {schedule.next_due_date && (
          <div className="text-right">
            <div className={`text-sm font-medium ${isOverdue ? 'text-red-700' : isDueSoon ? 'text-amber-700' : 'text-gray-700'}`}>
              {fmt.date(schedule.next_due_date)}
            </div>
            <div className={`text-xs ${isOverdue ? 'text-red-500' : isDueSoon ? 'text-amber-500' : 'text-gray-500'}`}>
              {isOverdue
                ? `${Math.abs(daysUntil)} jour${Math.abs(daysUntil) > 1 ? 's' : ''} de retard`
                : daysUntil === 0
                  ? "Aujourd'hui"
                  : `dans ${daysUntil} jour${daysUntil > 1 ? 's' : ''}`}
            </div>
          </div>
        )}
        {/* Due km */}
        {schedule.next_due_km && (
          <div className="text-right">
            <div className="text-sm font-medium text-gray-700">
              {fmt.number(schedule.next_due_km)} km
            </div>
            <div className="text-xs text-gray-500">
              Int. {schedule.interval_km != null ? fmt.number(schedule.interval_km) : '-'} km
            </div>
          </div>
        )}
        {/* Edit button */}
        <button
          onClick={() => onEdit(schedule)}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          title="Modifier"
        >
          <Edit2 size={14} className="text-gray-500" />
        </button>
      </div>
    </div>
  );
}

export function VehicleResourcesTab({ entity, entityType, onMessage, onUpdate }) {
  const vehicle = entity;
  const queryClient = useQueryClient();

  // Data state
  const [currentResources, setCurrentResources] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [entryMode, setEntryMode] = useState('single'); // 'single' or 'bulk'
  const [selectedType, setSelectedType] = useState(null);

  // Check if diesel for AdBlue
  const isDiesel = vehicle?.fuel_type === 'diesel';

  // Load data
  const loadData = useCallback(async () => {
    if (!vehicle?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Use Promise.allSettled to avoid one failure killing all data
      const [resourcesResult, schedulesResult, alertsResult] = await Promise.allSettled([
        vehicleResourceAPI.getCurrentByVehicle(vehicle.id),
        vehicleResourceAPI.getSchedule(vehicle.id),
        vehicleResourceAPI.getAlerts(vehicle.id),
      ]);

      // Extract data from settled results (fulfilled or empty fallback)
      const resourcesRes = resourcesResult.status === 'fulfilled' ? resourcesResult.value : {};
      const schedulesRes = schedulesResult.status === 'fulfilled' ? schedulesResult.value : {};
      const alertsRes = alertsResult.status === 'fulfilled' ? alertsResult.value : {};

      const resData = resourcesRes.data || {};
      const alertData = alertsRes.data || {};

      setCurrentResources(resData.readings || []);
      setSchedules(schedulesRes.data || []);
      setAlerts([...(alertData.level_alerts || []), ...(alertData.schedule_alerts || [])]);

      // Log any failures for debugging
      if (resourcesResult.status === 'rejected') console.warn('Resources fetch failed:', resourcesResult.reason);
      if (schedulesResult.status === 'rejected') console.warn('Schedules fetch failed:', schedulesResult.reason);
      if (alertsResult.status === 'rejected') console.warn('Alerts fetch failed:', alertsResult.reason);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError('Erreur lors du chargement des ressources');
    } finally {
      setLoading(false);
    }
  }, [vehicle?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get resource by type
  const getResource = (type) => currentResources.find((r) => r.resource_type === type);

  // Handle entry form submit
  const handleEntrySubmit = async (data) => {
    try {
      if (entryMode === 'bulk') {
        await vehicleResourceAPI.createBulkReadings(vehicle.id, data.readings, data.odometer_km);
        onMessage?.('Inspection enregistrée avec succès', 'success');
      } else {
        await vehicleResourceAPI.createReading(vehicle.id, data);
        onMessage?.('Mesure enregistrée avec succès', 'success');
      }
      await loadData();
      // Invalidate fleet-wide alerts so vehicle table pills update
      queryClient.invalidateQueries({ queryKey: ['resourceAlertsSummary'] });
    } catch (err) {
      throw err;
    }
  };

  // Open single entry form
  const openSingleEntry = (type = null) => {
    setEntryMode('single');
    setSelectedType(type);
    setShowEntryForm(true);
  };

  // Open bulk entry form
  const openBulkEntry = () => {
    setEntryMode('bulk');
    setSelectedType(null);
    setShowEntryForm(true);
  };

  // Handle schedule edit
  const handleEditSchedule = (schedule) => {
    // TODO: Implement schedule editor modal
    console.log('Edit schedule:', schedule);
    onMessage?.('Fonctionnalité à venir', 'info');
  };

  // Count alerts by severity
  const alertCounts = {
    critical: alerts.filter((a) => a.status === 'critical').length,
    warning: alerts.filter((a) => a.status === 'warning').length,
    low: alerts.filter((a) => a.status === 'low').length,
    total: alerts.length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
        <button onClick={loadData} className="ml-2 underline">
          Réessayer
        </button>
      </div>
    );
  }

  // Group fluids and wear items
  const fluidTypes = ['fuel_level', 'adblue_level', 'oil_level', 'brake_fluid', 'coolant_level', 'washer_fluid'];
  const wearTypes = ['brake_pad_front', 'brake_pad_rear', 'tire_front_left', 'tire_front_right', 'tire_rear_left', 'tire_rear_right'];
  const hullTypes = ['windshield', 'body_condition', 'headlights', 'taillights', 'mirrors'];

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Gauge size={20} className="text-blue-600" />
          Ressources & Consommables
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw size={18} className="text-gray-500" />
          </button>
          <button
            onClick={openBulkEntry}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-gray-700"
          >
            <ClipboardCheck size={16} />
            <span className="text-sm">Inspection</span>
          </button>
          <button
            onClick={() => openSingleEntry()}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-white"
          >
            <Plus size={16} />
            <span className="text-sm">Ajouter mesure</span>
          </button>
        </div>
      </div>

      {/* Alert summary */}
      <ResourceAlertSummary
        alertCount={alertCounts.total}
        warningCount={alertCounts.warning}
        criticalCount={alertCounts.critical}
      />

      {/* Fluids section */}
      <ResourceSection
        title="Niveaux"
        icon={Droplet}
        defaultOpen={true}
        badge={
          alertCounts.total > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
              {alertCounts.total} alerte{alertCounts.total > 1 ? 's' : ''}
            </span>
          )
        }
      >
        <div className="flex flex-wrap gap-6 justify-center sm:justify-start">
          {fluidTypes.map((type) => {
            // Skip AdBlue for non-diesel vehicles
            if (type === 'adblue_level' && !isDiesel) return null;

            const resource = getResource(type);
            return (
              <ResourceGauge
                key={type}
                type={type}
                value={resource?.value_percent ?? 0}
                status={resource?.status || 'unknown'}
                lastUpdated={resource?.reading_date}
                onClick={() => openSingleEntry(type)}
              />
            );
          })}
        </div>
        {currentResources.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">
            Aucune donnée disponible. Cliquez sur un niveau pour ajouter une mesure.
          </p>
        )}
      </ResourceSection>

      {/* Wear items section */}
      <ResourceSection
        title="Usure"
        icon={Circle}
        defaultOpen={currentResources.some((r) => wearTypes.includes(r.resource_type))}
      >
        <div className="space-y-2">
          {wearTypes.map((type) => {
            const resource = getResource(type);
            return (
              <WearItemBar
                key={type}
                type={type}
                value={resource?.value_percent ?? 0}
                status={resource?.status || 'unknown'}
                lastUpdated={resource?.reading_date}
                onClick={() => openSingleEntry(type)}
              />
            );
          })}
        </div>
        {!currentResources.some((r) => wearTypes.includes(r.resource_type)) && (
          <p className="text-sm text-gray-500 text-center py-4">
            Aucune donnée d'usure disponible. Effectuez une inspection pour ajouter des mesures.
          </p>
        )}
      </ResourceSection>

      {/* Hull section */}
      <ResourceSection
        title="Carrosserie"
        icon={Shield}
        defaultOpen={currentResources.some((r) => hullTypes.includes(r.resource_type))}
      >
        <div className="space-y-2">
          {hullTypes.map((type) => {
            const resource = getResource(type);
            return (
              <WearItemBar
                key={type}
                type={type}
                value={resource?.value_percent ?? 0}
                status={resource?.status || 'unknown'}
                lastUpdated={resource?.reading_date}
                onClick={() => openSingleEntry(type)}
              />
            );
          })}
        </div>
        {!currentResources.some((r) => hullTypes.includes(r.resource_type)) && (
          <p className="text-sm text-gray-500 text-center py-4">
            Aucune donnee carrosserie. Effectuez une inspection pour ajouter des mesures.
          </p>
        )}
      </ResourceSection>

      {/* Lifecycle schedules section */}
      <ResourceSection
        title="Prochaines échéances"
        icon={Calendar}
        defaultOpen={schedules.length > 0}
        badge={
          schedules.some((s) => s.days_until_due !== null && s.days_until_due <= 14) && (
            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
              <Clock size={12} className="inline mr-1" />
              Échéances proches
            </span>
          )
        }
      >
        {schedules.length > 0 ? (
          <div className="space-y-2">
            {schedules
              .sort((a, b) => {
                // Sort by days until due (null at end)
                if (a.days_until_due === null) return 1;
                if (b.days_until_due === null) return -1;
                return a.days_until_due - b.days_until_due;
              })
              .map((schedule) => (
                <ScheduleRow
                  key={schedule.id}
                  schedule={schedule}
                  onEdit={handleEditSchedule}
                />
              ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">
            Aucune échéance programmée.
          </p>
        )}
      </ResourceSection>

      {/* Entry form modal */}
      <ResourceEntryForm
        isOpen={showEntryForm}
        onClose={() => setShowEntryForm(false)}
        onSubmit={handleEntrySubmit}
        vehicleId={vehicle?.id}
        vehicleFuelType={vehicle?.fuel_type}
        vehicleMileage={vehicle?.mileage || 0}
        mode={entryMode}
        preselectedType={selectedType}
      />
    </div>
  );
}

export default VehicleResourcesTab;
